<script lang="ts">
  import { type Snippet } from "svelte";

  interface Props {
    variant?: "primary" | "secondary" | "ghost" | "ghost-danger";
    size?: "sm" | "md" | "lg";
    disabled?: boolean;
    loading?: boolean;
    type?: "button" | "submit" | "reset";
    class?: string;
    onclick?: (e: MouseEvent) => void;
    children?: Snippet;
    "data-testid"?: string;
    "aria-label"?: string;
  }

  let {
    variant = "secondary",
    size = "md",
    disabled = false,
    loading = false,
    type = "button",
    class: className = "",
    onclick,
    children,
    "data-testid": dataTestId,
    "aria-label": ariaLabel
  }: Props = $props();

  const sizeClasses: Record<string, string> = {
    sm: "text-xs px-2 py-0.5",
    md: "text-[0.875rem] px-3 min-h-[44px]",
    lg: "text-base px-4 py-2"
  };

  const variantClasses: Record<string, string> = {
    primary:
      "bg-accent text-chrome-bg border-0 font-body uppercase tracking-[0.05em] font-semibold hover:bg-accent-dim hover:text-text-primary",
    secondary:
      "bg-transparent border border-chrome-border text-text-secondary font-body uppercase tracking-[0.05em] hover:bg-chrome-bg-raised hover:text-text-primary",
    ghost: "bg-transparent border-0 text-text-secondary cursor-pointer hover:text-text-primary",
    "ghost-danger": "bg-transparent border-0 text-text-muted hover:text-status-danger"
  };

  let classes = $derived(
    [
      variantClasses[variant],
      sizeClasses[size],
      disabled || loading ? "opacity-50 cursor-not-allowed" : "",
      className
    ]
      .filter(Boolean)
      .join(" ")
  );
</script>

<button
  {type}
  class={classes}
  disabled={disabled || loading}
  onclick={disabled || loading ? undefined : onclick}
  aria-busy={loading}
  data-testid={dataTestId}
  aria-label={ariaLabel}
>
  {#if loading}
    <span class="inline-flex items-center gap-2">
      <span class="sr-only">Loading...</span>
      <svg
        class="animate-spin h-4 w-4"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"
        ></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
      {#if children}
        {@render children()}
      {/if}
    </span>
  {:else if children}
    {@render children()}
  {/if}
</button>
